import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent } from '../../shared/components/table/table.component';
import { TableColumn } from '../../core/models/column.model';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { Alert } from '../../core/models/alert.model';
import { GeneralComponent } from '../general/general.component';
import { UpdateRecordService } from '../../core/services/update-record.service';
import { MatDialog } from '@angular/material/dialog';
import { UpdateRecord } from '../../core/models/update-record.model';
import { ModalComponent } from '../modal/modal.component';
import { ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ShiftClosureService } from '../../core/services/shift-closure.service';
import { Constants } from '../../shared/utils/constants';
import { forkJoin } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-mis-novedades',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    RouterModule,
    GeneralComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './mis-novedades.component.html',
  styleUrl: './mis-novedades.component.scss',
})
export class MisNovedadesComponent implements OnInit {
  alerts: Alert[] = [];
  flattenedData: { [key: string]: any[] } = {
    [Constants.SEGURIDAD]: [],
    [Constants.OPERACION]: [],
    [Constants.PERSONAL]: [],
    [Constants.DESPACHO]: [],
  };

  tableColumns: TableColumn[] = [
    { header: 'Seleccionar', field: 'isMarkedForClosure', isCheckbox: true, showSelectAll: true, width: '120px' },
    { header: 'Hora', field: 'hora', width: '100px' },
    { header: 'Novedad', field: 'novedad', width: '60%' },
    { header: 'Fecha', field: 'fecha', isDate: true, width: '120px' },
  ];

  tableSeguridadColumns: TableColumn[] = [
    { header: 'Seleccionar', field: 'isMarkedForClosure', isCheckbox: true, showSelectAll: true, width: '120px' },
    { header: 'Hora', field: 'hora', width: '100px' },
    { header: 'Tipo', field: 'tipo', width: '150px' },
    { header: 'Novedad', field: 'novedad', width: '50%' },
    { header: 'Fecha', field: 'fecha', isDate: true, width: '120px' },
  ];

  constructor(
    private updateRecordService: UpdateRecordService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private shiftClosureService: ShiftClosureService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    console.log('[MisNovedades] Inicializando componente...');
    this.loadData('personal');
    this.loadData('seguridad');
    console.log('[MisNovedades] Componente inicializado, cargando datos...');
  }

  loadData(type: string): void {
    console.log(`[MisNovedades] Cargando datos para tipo: ${type}`);
    // Usar servicio con userId y áreas
  this.updateRecordService.getUpdateRecordsByTypeAndAreas(type, ['general'], false, undefined, false, true).subscribe({
      next: (updates: UpdateRecord[]) => {
        console.log(`[MisNovedades] Datos recibidos para ${type}:`, updates);
        const flattened = this.flattenData(updates);
        this.flattenedData[type] = flattened.length ? flattened : [];
        console.log(`[MisNovedades] Datos procesados para ${type}:`, this.flattenedData[type]);
      },
      error: error => {
        console.error(`[MisNovedades] Error al cargar datos para ${type}:`, error);
        if (error.status === 404) {
          this.flattenedData[type] = [];
          console.log(`[MisNovedades] 404 - Inicializando array vacío para ${type}`);
        } else {
          console.error(`Error al cargar los datos para el tipo ${type}:`, error);
          this.flattenedData[type] = [];
        }
      },
    });
  }

  newUpdateRecord(type: string) {
    // Determinar el área basado en el tipo
    const area = 'general';
    
    // Verificar si hay un turno abierto (scoped por usuario) antes de abrir el modal
    this.shiftClosureService.getOpenShiftByType('informe_turno').subscribe({
      next: (shiftId) => {
        if (!shiftId) {
          alert('Error: No existe un turno abierto para registrar información');
          return;
        }
        const dialogRef = this.dialog.open(ModalComponent, {
          data: { type, area },
          width: '600px',
        });
        dialogRef.afterClosed().subscribe((result: { success: boolean }) => {
          if (result?.success) {
            this.loadData(type);
          }
        });
      },
      error: (error) => {
        if (error?.status === 404) {
          alert('Error: No existe un turno abierto para registrar información');
        } else {
          console.error('Error al verificar el turno abierto:', error);
          alert('Ocurrió un error al verificar el turno. Intenta nuevamente.');
        }
      }
    });
  }
  editUpdateRecord(updateRecord: any): void {
    if (!updateRecord.updateElements) {
      console.error('El objeto no tiene elementos de actualización:', updateRecord);
      return;
    }

    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        record: updateRecord, // Pasar los datos del registro
        readOnly: false, // Permitir edición
      },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((result: { success: boolean }) => {
      if (result?.success) {
        console.log('Edición realizada con éxito. Actualizando datos...');
        this.loadData('seguridad');
        this.loadData('personal');
      }
    });
  }

  deleteUpdateRecord(id: number): void {
    if (confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      this.updateRecordService.deleteUpdateRecord(id).subscribe({
        next: () => {
          console.log(`Registro con ID ${id} eliminado correctamente.`);
          this.loadData('seguridad');
          this.loadData('personal');
        },
        error: error => {
          console.error(`Error al eliminar el registro con ID ${id}:`, error);
        },
      });
    }
  }

  // Determina la marca de cierre a mostrar según rol (sin fallback por área)
  private getRoleSpecificClosureMark(update: UpdateRecord): boolean {
    const u: any = update as any;
    const isDcs = this.authService.hasRole('Novedades_Operador_DCS') || this.authService.hasRole('DCS') || this.authService.hasRole('dcs');
    const isFerrico = this.authService.hasRole('Novedades_Operador_Ferrico') || this.authService.hasRole('FERRICO') || this.authService.hasRole('ferrico');
    if (isDcs) {
      return (u.isMarkedForClosureDCS ?? u.isMarkedForClosureDcs ?? update.isMarkedForClosure) as boolean;
    }
    if (isFerrico) {
      return (u.isMarkedForClosureFerrico ?? update.isMarkedForClosure) as boolean;
    }
    return update.isMarkedForClosure;
  }

  flattenData(updates: UpdateRecord[]): any[] {
    return updates.flatMap(update => {
      const horaElement = update.updateElements.find(el => el.label === 'Hora' || el.label === 'Hora Inicio');
      const tipoElement = update.updateElements.find(el => el.label === 'Tipo de Novedad'); // Buscar el tipo
      let novedadElement, novedad;
      novedadElement = update.updateElements.find(el => el.label === 'Novedad');
      novedad = update.updateElements
        .filter(element => !(element.label === 'Hora' || element.element.name === 'Label'))
        .map(element => {
          // Concatenar el label con los valores de updateValues
          const values = element.updateValues
            .map(val => {
              let display;
              if (val.value === "true") display = "Si";
              else if (val.value === "false") display = "No";
              else display = val.value;
              return `${element.label}: ${display}`;
            })
            .join(', ');
          return values || `${element.label}: N/A`; // Si no hay valores, mostrar el label con N/A
        })
        .filter(text => text) // Eliminar elementos vacíos
        .join(', '); // Unir todos los elementos con coma
      return {
        id: update.id,
        hora: horaElement?.updateValues[0]?.value || '',
        responsable:
          update.updateElements?.find(el => el.element?.id === 2 || el.element?.id === 5)
            ?.updateValues[0]?.createdUser?.username || '',
        label: update.updateType?.name || '',
        novedad: novedadElement ? novedadElement.updateValues[0]?.value || '' : novedad,
        tipo: tipoElement?.updateValues[0]?.value || '',
        fecha:
          update.updateElements?.find(el => el.element?.id === 2 || el.element?.id === 5)
            ?.updateValues[0]?.createdUtc || '',
        updateElements: update.updateElements,
        updateType: update.updateType,
        updateArea: update.updateArea,
        isMarkedForClosure: this.getRoleSpecificClosureMark(update),
      };
    });
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  viewUpdateRecord(updateRecord: UpdateRecord): void {
    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        record: updateRecord, // Pasar el objeto directamente
        readOnly: true, // Abrir en modo solo lectura
      },
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  onCheckboxChange(isMarked: boolean, updateRecordId: number): void {
    // Find the item in the data and temporarily update its state
    const dataArrays = [this.flattenedData[Constants.SEGURIDAD], this.flattenedData[Constants.PERSONAL]];
    let targetItem: any = null;
    
    for (const dataArray of dataArrays) {
      targetItem = dataArray.find(item => item.id === updateRecordId);
      if (targetItem) break;
    }

    if (targetItem) {
      const previousState = targetItem.isMarkedForClosure;
      targetItem.isMarkedForClosure = isMarked; // Optimistically update UI
      
      this.updateRecordService.updateMarkForClosure(updateRecordId, isMarked).subscribe({
        next: () => {
          console.log('Estado actualizado correctamente.');
        },
        error: error => {
          console.error('Error al actualizar el estado:', error);
          // Revert the UI state on error
          targetItem.isMarkedForClosure = previousState;
        },
      });
    }
  }

  onSelectAllChange(event: { checked: boolean; type: string; visibleData?: any[] }): void {
    const { checked, type } = event;
    let targetData: any[] = [];

    // Si tenemos visibleData, usar solo los datos visibles; si no, usar todos los datos
    if (event.visibleData && event.visibleData.length > 0) {
      targetData = event.visibleData;
    } else {
      // Determine which data array to work with based on the type
      if (type === 'seguridad') {
        targetData = this.flattenedData[Constants.SEGURIDAD];
      } else if (type === 'personal') {
        targetData = this.flattenedData[Constants.PERSONAL];
      }
    }

    if (targetData.length === 0) return;

    // Filtrar solo los elementos que realmente necesitan cambio de estado
    const itemsToUpdate = targetData.filter(item => {
      return item && item.id && item.isMarkedForClosure !== checked;
    });

    if (itemsToUpdate.length === 0) {
      console.log('Todos los elementos visibles ya tienen el estado deseado.');
      return;
    }

    // Store previous states for potential rollback (solo para elementos que van a cambiar)
    const previousStates = itemsToUpdate.map(item => ({
      id: item.id,
      isMarkedForClosure: item.isMarkedForClosure
    }));

    // Optimistically update only items that need to change
    itemsToUpdate.forEach(item => {
      item.isMarkedForClosure = checked;
    });

    // Create API calls only for items that need to change
    const apiCalls = itemsToUpdate.map(item =>
      this.updateRecordService.updateMarkForClosure(item.id, checked)
    );

    // Execute all API calls in parallel
    forkJoin(apiCalls).subscribe({
      next: (results) => {
        console.log(`Todos los elementos de ${type} fueron actualizados correctamente.`);
      },
      error: (error) => {
        console.error(`Error al actualizar elementos de ${type}:`, error);
        // Revert UI states on any error (solo para elementos que se intentaron cambiar)
        previousStates.forEach(prevState => {
          const item = itemsToUpdate.find(dataItem => dataItem.id === prevState.id);
          if (item) {
            item.isMarkedForClosure = prevState.isMarkedForClosure;
          }
        });
      }
    });
  }
}
